import React from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { RotateCw, Lightbulb } from 'lucide-react';

/**
 * A label cell butted against a value cell — one input, one row, self-contained
 * so it reads the same whether it sits in a column or across a band.
 */
export function Field({ label, suffix, children, className }) {
  return (
    <div className={cn(
      'flex min-w-0 items-stretch overflow-hidden rounded border border-slate-200 text-xs', className
    )}>
      <span className="flex min-w-[5.5rem] shrink-0 items-center whitespace-nowrap border-r border-slate-200 bg-slate-100 px-2 py-1 font-medium text-slate-600">
        {label}
      </span>
      <span className="flex min-w-0 flex-1 items-center gap-1 pr-1.5">
        {children}
        {suffix && <span className="shrink-0 text-[10px] text-slate-400">{suffix}</span>}
      </span>
    </div>
  );
}

export function NumberField({ label, suffix, value, onChange, placeholder }) {
  return (
    <Field label={label} suffix={suffix}>
      <Input type="number" value={value ?? ''} placeholder={placeholder ?? '0'}
        onChange={e => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
        className="h-6 border-0 px-1.5 text-xs tabular-nums shadow-none focus-visible:ring-1" />
    </Field>
  );
}

/**
 * A named set of fields that wraps across whatever width it is given, so the
 * inputs run along the top of the screen instead of down one scrolling column.
 */
export function FieldGroup({ title, aside, min = '11rem', children, className }) {
  return (
    <section className={cn('min-w-0', className)}>
      <p className="flex items-baseline justify-between gap-2 px-0.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        <span>{title}</span>
        {aside && <span className="truncate font-normal normal-case tracking-normal">{aside}</span>}
      </p>
      <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}, 1fr))` }}>
        {children}
      </div>
    </section>
  );
}

export function StyleSelect({ styles, value, onChange }) {
  const grouped = styles.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-6 border-0 px-1.5 text-xs shadow-none" data-testid="box-style-select">
        <SelectValue placeholder="Pick a box style" />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(grouped).map(([category, list]) => (
          <React.Fragment key={category}>
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400">
              {category.replace('_', ' ')}
            </div>
            {list.map(s => (
              <SelectItem key={s.code} value={s.code} className="text-xs">
                <span className="font-mono text-slate-400 mr-1.5">{s.code}</span>{s.name}
              </SelectItem>
            ))}
          </React.Fragment>
        ))}
      </SelectContent>
    </Select>
  );
}

export const fmt = (v, d = 0) => Number(v ?? 0).toLocaleString(undefined, {
  minimumFractionDigits: d, maximumFractionDigits: d,
});

/**
 * Which way round the blank goes, and what to trim to gain an up.
 *
 * The headline numbers moved to the summary strip at the top of the screen; what
 * is left here is the part you act on — the two orientations side by side and
 * the advisory that says how close the losing one is.
 */
export function LayoutAdvisory({ result, onRotate, forced }) {
  if (!result) return null;
  const imp = result.imposition;
  const chosen = imp.recommended_orientation === 'rotated' ? imp.rotated : imp.upright;

  return (
    <div className="space-y-2 p-2">
      <div className="grid grid-cols-2 gap-1.5">
        {[['Upright', imp.upright], ['Rotated 90°', imp.rotated]].map(([label, o]) => (
          <div key={label} className={cn(
            'rounded border p-2',
            o === chosen ? 'border-brand-blue bg-blue-50' : 'border-slate-200 bg-white'
          )}>
            <p className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-400">
              {label}
              {o === chosen && <span className="text-brand-blue">in use</span>}
            </p>
            <p className="text-lg font-bold leading-tight tabular-nums text-slate-800">
              {o.ups_across_width} × {o.ups_along_length} = {o.total_ups}
            </p>
            <p className="text-[10px] text-slate-400">
              {fmt(o.unit_width_mm, 1)} × {fmt(o.unit_length_mm, 1)} mm per up
            </p>
          </div>
        ))}
      </div>

      <div className="space-y-2 rounded border border-amber-200 bg-amber-50 p-2.5">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-800">
          <Lightbulb className="h-3.5 w-3.5" /> Layout advisory
        </p>
        <p className="text-xs leading-relaxed text-amber-900">{chosen.advice_width}</p>
        <p className="text-xs leading-relaxed text-amber-900">{chosen.advice_length}</p>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-amber-200 pt-1.5">
          <p className="min-w-0 flex-1 text-xs text-amber-900">{imp.rotation_advice}</p>
          <button type="button" onClick={onRotate} data-testid="rotate-btn"
            className="flex shrink-0 items-center gap-1 rounded border border-amber-300 bg-white px-2 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-100">
            <RotateCw className="h-3 w-3" />
            {forced === 'rotated' ? 'Back to upright' : 'Rotate 90°'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The priced rows. Nothing here is typed — every quantity comes off the imposition. */
export function CostSheet({ sheet, rateCount }) {
  if (!sheet) {
    return <p className="px-3 py-6 text-center text-xs text-slate-400">Enter the box and the sheet.</p>;
  }
  if (!sheet.rows.length) {
    return (
      <p className="px-3 py-6 text-center text-xs text-slate-400">
        {rateCount === 0
          ? 'No rates in the master yet. Add them under Rate Master and the cost sheet prices itself.'
          : 'Pick a cost group to price this job.'}
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
            <th className="px-2 py-1.5 text-left">Process</th>
            <th className="px-2 py-1.5 text-right">Units</th>
            <th className="px-2 py-1.5 text-left">Unit</th>
            <th className="px-2 py-1.5 text-right">Rate</th>
            <th className="px-2 py-1.5 text-right">Per</th>
            <th className="px-2 py-1.5 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {sheet.rows.map((row, idx) => (
            <tr key={`${row.id || row.process}-${idx}`} className="border-t border-slate-200">
              <td className="px-2 py-1.5">
                {row.process}
                {row.remark && <span className="ml-1.5 text-[10px] text-slate-400">{row.remark}</span>}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmt(row.units, 2)}</td>
              <td className="px-2 py-1.5 text-slate-500">{row.unit}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmt(row.rate, 2)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">{fmt(row.per)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                {fmt(row.amount, 2)}
                {row.minimum_applied && (
                  <Badge className="ml-1.5 bg-amber-50 text-amber-700 text-[9px]">min</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-slate-50">
          {[
            ['Subtotal', sheet.subtotal],
            [`Wastage ${sheet.wastage_pct}%`, sheet.wastage_amount],
            [`Margin ${sheet.margin_pct}%`, sheet.margin_amount],
          ].map(([label, value]) => (
            <tr key={label} className="border-t border-slate-200">
              <td colSpan={5} className="px-2 py-1 text-right text-slate-500">{label}</td>
              <td className="px-2 py-1 text-right tabular-nums">{fmt(value, 2)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-brand-blue">
            <td colSpan={5} className="px-2 py-2 text-right font-semibold">Total</td>
            <td className="px-2 py-2 text-right tabular-nums font-bold text-brand-blue">₹{fmt(sheet.total, 2)}</td>
          </tr>
          <tr>
            <td colSpan={5} className="px-2 pb-2 text-right text-slate-500">Cost per unit</td>
            <td className="px-2 pb-2 text-right tabular-nums font-semibold">₹{fmt(sheet.cost_per_unit, 4)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
